import moment from 'moment'
import {ShipmentTrendBreakout, TimeRange, TREND_SERIES_COLORS} from '../core/_const'
import {ShipmentTrendPoint, ShipmentTrendSeries} from '../core/_types'

export type {ShipmentTrendBreakout} from '../core/_const'
export {SHIPMENT_TREND_BREAKOUTS} from '../core/_const'

const GOLDEN_ANGLE = 137.508
const COLOR_GENERATE_ATTEMPTS = 30

export type OrganizationAnalyticsFilters = {
  accountIds: Array<string | number>
  timeRange: TimeRange
  isCustomRange: boolean
  dateRange: {start: moment.Moment; end: moment.Moment}
}

export type ShipmentTrendDataByBreakout = {
  overall: ShipmentTrendPoint[]
  carrier: ShipmentTrendSeries[]
  service: ShipmentTrendSeries[]
}

export const EMPTY_SHIPMENT_TREND_DATA: ShipmentTrendDataByBreakout = {
  overall: [],
  carrier: [],
  service: [],
}

export const isMultiSeriesBreakout = (
  breakout: ShipmentTrendBreakout
): breakout is 'carrier' | 'service' => breakout !== 'overall'

export const isBreakoutTrendSeries = (
  data: ShipmentTrendPoint[] | ShipmentTrendSeries[]
): data is ShipmentTrendSeries[] => data.length > 0 && 'data' in data[0]

export const getTrendSeriesKey = (series: ShipmentTrendSeries) =>
  series.carrier ?? series.service ?? series.label

const hashString = (value: string) => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const generatedColorFromSeed = (seed: number, attempt: number) => {
  const hue = Math.round((seed + attempt * GOLDEN_ANGLE) % 360)
  const saturation = 58 + (attempt % 3) * 6
  const lightness = 42 + (attempt % 2) * 8
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

export const buildTrendSeriesColorMap = (series: ShipmentTrendSeries[]) => {
  const colorMap: Record<string, string> = {}
  const usedColors = new Set<string>()

  series.forEach((item, index) => {
    const key = getTrendSeriesKey(item)

    if (index < TREND_SERIES_COLORS.length) {
      const paletteColor = TREND_SERIES_COLORS[index]
      colorMap[key] = paletteColor
      usedColors.add(paletteColor.toLowerCase())
      return
    }

    const seed = hashString(key)
    let nextColor = generatedColorFromSeed(seed, 0)

    for (let attempt = 0; attempt < COLOR_GENERATE_ATTEMPTS; attempt += 1) {
      const candidate = generatedColorFromSeed(seed, attempt)
      const normalized = candidate.toLowerCase()

      if (!usedColors.has(normalized)) {
        nextColor = candidate
        break
      }
    }

    colorMap[key] = nextColor
    usedColors.add(nextColor.toLowerCase())
  })

  return colorMap
}

export const normalizeTrendSeries = (series: ShipmentTrendSeries[]) => {
  const seen = new Set<string>()

  return series.filter((item) => {
    const key = getTrendSeriesKey(item)
    if (seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

export const clearTrendBreakoutData = (
  breakout: ShipmentTrendBreakout
): Partial<ShipmentTrendDataByBreakout> =>
  breakout === 'overall' ? {overall: []} : {[breakout]: []}

export const applyTrendResponse = (
  breakout: ShipmentTrendBreakout,
  data: ShipmentTrendPoint[] | ShipmentTrendSeries[]
): Partial<ShipmentTrendDataByBreakout> | null => {
  if (breakout === 'overall') {
    return {overall: data as ShipmentTrendPoint[]}
  }

  if (isBreakoutTrendSeries(data)) {
    return {[breakout]: normalizeTrendSeries(data)}
  }

  return null
}

export const getActiveTrendSeries = (
  activeView: ShipmentTrendBreakout,
  trendData: ShipmentTrendDataByBreakout
) => (isMultiSeriesBreakout(activeView) ? trendData[activeView] : [])

export const hasTrendChartData = (
  activeView: ShipmentTrendBreakout,
  trendData: ShipmentTrendDataByBreakout
) =>
  isMultiSeriesBreakout(activeView)
    ? trendData[activeView].some((series) => series.data.length > 0)
    : trendData.overall.length > 0

export const buildOrganizationAnalyticsPayload = (
  {accountIds, timeRange, isCustomRange, dateRange}: OrganizationAnalyticsFilters,
  breakout?: ShipmentTrendBreakout
) => {
  const payload: Record<string, unknown> = {
    account_ids: accountIds,
    period: isCustomRange ? 'custom' : timeRange,
  }

  if (breakout) {
    payload.breakout = breakout
  }

  if (isCustomRange) {
    payload.start_date = dateRange.start.clone().startOf('day').format()
    payload.end_date = dateRange.end.clone().endOf('day').format()
  }

  return payload
}
